import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, ShoppingCart, Package, Download, Lock } from "lucide-react";
import { coverUrl, packTrackToPlayable, fmtDuration } from "@/lib/api";
import BizumModal from "@/components/BizumModal";
import { useAuth } from "@/context/AuthContext";

export default function PackShowcase({ pack, config }) {
  const [bizumOpen, setBizumOpen] = useState(false);
  const auth = useAuth();
  const playable = (pack.tracks || []).map((pt) => packTrackToPlayable(pack, pt));
  const isFree = pack.price === 0;

  return (
    <>
      <div data-testid={`pack-showcase-${pack.id}`}
        className="group relative bg-[#16161D] border border-white/5 rounded-xl overflow-hidden card-glow transition-all duration-300 grid sm:grid-cols-[14rem_minmax(0,1fr)]">
        {pack.cover_path && (
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true" data-testid={`pack-showcase-bg-${pack.id}`}>
            <img src={coverUrl(pack.cover_path, 160)} alt="" className="w-full h-full object-cover blur-2xl scale-125 opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d12]/40 via-[#0d0d12]/60 to-[#0d0d12]/85"></div>
          </div>
        )}

        <div className="relative flex flex-col">
          <Link to={`/pack/${pack.id}`} data-testid={`pack-showcase-cover-${pack.id}`}
            className="relative block aspect-square sm:h-56 bg-neutral-900 overflow-hidden">
            {pack.cover_path ? (
              <img src={coverUrl(pack.cover_path, 1200)} alt={pack.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-red-900/40 via-black to-black flex items-center justify-center">
                <Package className="w-10 h-10 text-red-600/50" />
              </div>
            )}
          </Link>
          <div className="p-3 flex flex-col gap-2" data-testid={`pack-showcase-actions-${pack.id}`}>
            <Link to={`/pack/${pack.id}`} data-testid={`pack-showcase-play-${pack.id}`}
              className="flex items-center justify-center gap-2 py-2 px-4 rounded-full btn-red text-sm font-semibold text-white">
              <Play className="w-4 h-4" /> Escuchar
            </Link>
            <div className="flex items-stretch gap-2">
              <span data-testid={`pack-price-badge-${pack.id}`}
                className={`flex-1 flex items-center justify-center py-1.5 text-xs font-mono uppercase tracking-widest rounded-full border ${
                  isFree ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-600/25 text-red-400 border-red-600/40"
                }`}>
                {isFree ? "Free" : `€${pack.price.toFixed(2)}`}
              </span>
              {pack.coming_soon && <span data-testid={`pack-showcase-coming-soon-${pack.id}`} className="flex-1 flex items-center justify-center py-1.5 rounded-full border border-amber-600/50 bg-amber-950/30 text-amber-400 text-[10px] font-mono uppercase tracking-widest">Próximamente</span>}
              {!isFree && !pack.coming_soon && (
                <button onClick={() => setBizumOpen(true)} data-testid={`pack-buy-button-${pack.id}`}
                  className="flex-1 flex items-center justify-center gap-2 py-1.5 px-3 rounded-full bg-white/5 hover:bg-red-600/20 border border-white/10 hover:border-red-600/50 text-xs font-semibold text-white transition-all">
                  <ShoppingCart className="w-3.5 h-3.5" /> Comprar
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="relative flex flex-col min-w-0 border-t sm:border-t-0 sm:border-l border-white/5 sm:h-[20.5rem]">
          <div className="px-5 pt-4 pb-3 flex items-end justify-between gap-4">
            <div className="min-w-0">
              <span className="font-mono text-[10px] text-red-400 uppercase tracking-widest">Pack · {playable.length || pack.track_count || "?"} tracks</span>
              <Link to={`/pack/${pack.id}`}>
                <h3 className="font-display font-black text-lg sm:text-xl uppercase tracking-tight text-white leading-tight mt-0.5 hover:text-red-500 transition-colors truncate">{pack.title}</h3>
              </Link>
              {pack.description && <p className="text-xs text-white/50 mt-1 line-clamp-1">{pack.description}</p>}
            </div>
          </div>

          <div className="relative border-t border-white/5 bg-black/30 backdrop-blur-sm flex-1 min-h-0 overflow-y-auto" data-testid={`pack-showcase-playlist-${pack.id}`}>
            {playable.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-white/30 py-10">Sin pistas sueltas · descarga el ZIP</div>
            ) : playable.map((t, i) => (
              <Link key={t.id} to={`/pack/${pack.id}`} data-testid={`pack-showcase-row-${t.id}`}
                className="flex items-center gap-3 px-4 py-2 border-b border-white/[0.03] last:border-0 transition-colors hover:bg-white/[0.03]">
                <span className="font-mono text-[10px] text-white/30 w-5 text-right">{String(i + 1).padStart(2, "0")}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold truncate text-white/90">{t.title}</div>
                  <div className="text-[10px] text-white/80 truncate">{t.artist}{(t.versions || []).length > 0 && <span className="text-pink-400 font-semibold ml-2">{t.versions.length + 1} versiones</span>}</div>
                </div>
                <div className="hidden md:flex items-center gap-1 shrink-0" data-testid={`pack-showcase-tags-${t.id}`}>
                  {(t.tags || []).map((tag) => (
                    <span key={tag} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border text-white/60 border-white/10">{tag}</span>
                  ))}
                  {t.bpm && <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border text-sky-400 border-sky-800/50 bg-sky-950/40">{t.bpm} BPM</span>}
                </div>
                <span className="font-mono text-[10px] text-white/40 w-9 text-right">{fmtDuration(t.duration)}</span>
                {isFree ? (
                  <a href={t.download_url} title="Descargar pista" data-testid={`pack-showcase-track-download-${t.id}`}
                    onClick={(e) => { e.stopPropagation(); auth.requireAuth(e); }}
                    className="p-1 text-white/40 hover:text-red-500"><Download className="w-3.5 h-3.5" /></a>
                ) : (
                  <span className="p-1 text-white/20"><Lock className="w-3.5 h-3.5" /></span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
      <BizumModal open={bizumOpen} onClose={() => setBizumOpen(false)} item={pack} itemType="pack" config={config} />
    </>
  );
}
